import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { mockData } from '../mock';

const Footer = () => {
  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Training', href: '/training' },
    { name: 'Gallery', href: '/gallery' },
  ];

  const services = [
    'Paint Protection Film',
    'Ceramic Coating',
    'Premium Detailing',
    'Training Programs',
  ];

  const drivelineLinks = [
    { name: 'Overview', href: '/driveline', testid: 'footer-driveline-overview' },
    { name: 'Pricing', href: '/driveline/pricing', testid: 'footer-driveline-pricing' },
    { name: 'Guides', href: '/driveline/guides', testid: 'footer-driveline-guides' },
    { name: 'Refund & Cancellation', href: '/driveline/refund-policy', testid: 'footer-driveline-refund' },
    { name: 'Start free trial', href: '/get-started', testid: 'footer-driveline-trial' },
  ];

  return (
    <footer className="bg-slate-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex flex-col">
              <h3 className="text-xl font-bold text-gray-900 leading-tight">UNISOL</h3>
              <h4 className="text-sm font-medium text-gray-700 -mt-1">GLOBAL</h4>
              <p className="text-xs text-gray-600 mt-1">{mockData.company.tagline}</p>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              Leading provider of premium automotive detailing services and professional training programs. 
              Serving luxury vehicle owners and aspiring entrepreneurs across India.
            </p>
            <div className="text-teal-600 font-semibold text-sm">
              {mockData.company.established}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-teal-600 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service} className="text-gray-600 text-sm">
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Driveline — workshop software */}
          <div data-testid="footer-driveline">
            <h4 className="text-lg font-semibold text-gray-900 mb-6">Driveline</h4>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              Our workshop management software, for other workshops.
            </p>
            <ul className="space-y-3">
              {drivelineLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-teal-600 transition-colors text-sm"
                    data-testid={link.testid}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-6">Contact Info</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="text-teal-600 mt-0.5" size={16} />
                <div className="text-sm">
                  <p className="text-gray-900 font-medium">Bangalore</p>
                  <p className="text-gray-600">Karnataka, India</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="text-teal-600" size={16} />
                <p className="text-gray-600 text-sm">{mockData.contact.bangalore.phone}</p>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="text-teal-600" size={16} />
                <p className="text-gray-600 text-sm">{mockData.contact.bangalore.email}</p>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="text-teal-600" size={16} />
                <p className="text-gray-600 text-sm">{mockData.contact.hours}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-600 text-sm">
              © 2024 {mockData.company.name}. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <Link to="/driveline/privacy" className="text-gray-600 hover:text-teal-600 text-sm transition-colors" data-testid="footer-privacy">
                Privacy Policy
              </Link>
              <Link to="/driveline/terms" className="text-gray-600 hover:text-teal-600 text-sm transition-colors" data-testid="footer-terms">
                Terms of Service
              </Link>
              <Link to="/driveline/refund-policy" className="text-gray-600 hover:text-teal-600 text-sm transition-colors" data-testid="footer-refund">
                Refund &amp; Cancellation
              </Link>
              <Link to="/driveline/pricing" className="text-gray-600 hover:text-teal-600 text-sm transition-colors" data-testid="footer-pricing">
                Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;